/**
 * The named color palette — **names and semantics only, never values**.
 *
 * A color anywhere in the language is one of three things:
 *
 *   - a **palette name** — a standard color word (`blue`, `crimson`, `teal`) or
 *     a semantic role (`info`, `warn`, `error`, `ok`, `accent`, `muted`);
 *   - a **literal CSS color** — `#f43`, `rgb(…)`, `hsl(…)`;
 *   - a **derived** color, from `colorize(<getter>)` — an arbitrary string
 *     hashed into the swatch ring below.
 *
 * The engine resolves the first and third forms to a NAME and stops there. It
 * deliberately holds no hex: concrete per-theme values live in
 * `@archlang/scene`'s `paletteColor()`, the one place both backends (live DOM
 * shell and `toSVG` export) read them from. That split is what lets the
 * `module` base kind default to `widget.color: info` without core knowing a
 * thing about CSS.
 */

/**
 * Semantic roles — the tone tier. A role names a *meaning*; the theme picks the
 * hue. Mirrors the `--au-info/-warn/-err/-ok` tones in `arch.ui/tokens.css`.
 */
export const PALETTE_ROLES: readonly string[] = [
  "accent",
  "info",
  "warn",
  "error",
  "ok",
  "muted",
];

/**
 * Concrete color words — the swatch tier. Standard color names, so authors
 * write what they already know (`widget.color: crimson`) rather than learning
 * invented brand words. Values are theme-tuned, so `red` on a dark board is a
 * legible red, not `#ff0000`.
 */
export const PALETTE_SWATCHES: readonly string[] = [
  "red",
  "green",
  "blue",
  "yellow",
  "orange",
  "purple",
  "gray",
  "grey",
  "pink",
  "brown",
  "cyan",
  "magenta",
  "lime",
  "teal",
  "navy",
  "maroon",
  "olive",
  "silver",
  "gold",
  "indigo",
  "violet",
  "crimson",
  "coral",
  "salmon",
  "khaki",
  "turquoise",
  "black",
  "white",
];

/** Every legal palette name — roles and swatches share one namespace. */
export const PALETTE_NAMES: ReadonlySet<string> = new Set([...PALETTE_ROLES, ...PALETTE_SWATCHES]);

/**
 * The **swatch ring** — the ordered subset `colorize()` hashes into.
 *
 * Not the whole swatch tier: hashing into `black`/`white`/`gray`/`silver` would
 * paint nodes in the board's own chrome colors, and near-duplicate hues
 * (`navy`≈`blue`, `maroon`≈`brown`) waste ring slots that should read as
 * "different thing". Ordering interleaves hue families so two adjacent ring
 * slots never look alike — relevant because the legend lists ring neighbours
 * side by side.
 *
 * 🚫 The ring's CONTENTS AND ORDER are a frozen contract, exactly like the hash
 * below: changing either repaints every existing diagram. Guarded by the
 * frozen-vector test in `__tests__/palette.test.ts`.
 */
export const SWATCH_RING: readonly string[] = [
  "blue",
  "orange",
  "green",
  "crimson",
  "violet",
  "turquoise",
  "gold",
  "pink",
  "teal",
  "coral",
  "indigo",
  "lime",
  "salmon",
  "cyan",
  "purple",
  "yellow",
  "brown",
  "magenta",
  "olive",
  "red",
];

/**
 * CSS keywords that are legal in a color slot but are not palette entries.
 *
 * `transparent` only. NOT `currentcolor`: whatever passes here is written
 * verbatim into an SVG paint attribute, and an exported tree sets no `color`
 * property for it to inherit — resvg paints it black. A keyword that needs a
 * stylesheet to mean anything is the same defect as a bare palette name.
 * (Icon bodies are unaffected; scene resolves `currentColor` inside them
 * separately — see scene/src/model.ts.)
 */
const CSS_COLOR_KEYWORDS: ReadonlySet<string> = new Set(["transparent"]);

/** Is `s` a palette name (role or swatch)? Case-insensitive, like CSS. */
export function isPaletteName(s: string): boolean {
  return PALETTE_NAMES.has(s.trim().toLowerCase());
}

/** A pragmatic CSS-color check: hex (#rgb/#rgba/#rrggbb/#rrggbbaa), a
 *  functional `rgb()/rgba()/hsl()/hsla()`, a palette name, or a CSS keyword. */
export function isCssColor(s: string): boolean {
  const t = s.trim().toLowerCase();
  if (/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(t)) return true;
  if (/^(rgb|rgba|hsl|hsla)\(/.test(t)) return true;
  return isPaletteName(t) || CSS_COLOR_KEYWORDS.has(t);
}

/**
 * FNV-1a over the value's code units.
 *
 * 🚫 FROZEN. The whole point of `colorize()` is that a team is the same color
 * in every view, in every export, and in a share link opened on another
 * machine — so this function may never change silently. A change is a
 * SPECIFICATION §14 delta and repaints every diagram in existence.
 */
function fnv1a(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * `colorize(<value>)` — an arbitrary string to its palette name, by stable
 * hash into `SWATCH_RING`.
 *
 * **Pure hash, no set-awareness.** The mapping depends on the string alone,
 * never on which other values happen to be present, so filtering a view or
 * streaming a board in never recolors the nodes that survive. The price is
 * collisions past `SWATCH_RING.length` distinct values; the legend still lists
 * every value against its swatch, so it stays honest. For maximum contrast
 * across a known set — at the cost of that stability — a view uses
 * `distinct(<getter>)` instead.
 */
export function swatchFor(value: string): string {
  return SWATCH_RING[fnv1a(value) % SWATCH_RING.length]!;
}
